import { useState } from 'react';
import Layout from '../../components/feature/Layout';
import { visitors, visitorTimeline, type Visitor } from '../../mocks/visitors';

const deviceIcons: Record<string, string> = { mobile: 'ri-smartphone-line', desktop: 'ri-computer-line', tablet: 'ri-tablet-line' };
const sourceColors: Record<string, string> = { google: 'bg-blue-50 text-blue-600', instagram: 'bg-pink-50 text-pink-600', facebook: 'bg-indigo-50 text-indigo-600', organic: 'bg-green-50 text-green-600', tiktok: 'bg-cyan-50 text-cyan-600', direct: 'bg-charcoal-50 text-charcoal-500' };
const eventIcons: Record<string, string> = { page_view: 'ri-eye-line', campaign_view: 'ri-megaphone-line', video_play: 'ri-play-circle-line', button_click: 'ri-cursor-line', whatsapp_click: 'ri-whatsapp-line', form_start: 'ri-file-edit-line', form_submit: 'ri-check-double-line' };
const eventColors: Record<string, string> = { page_view: '#64B5F6', campaign_view: '#D4AF37', video_play: '#FFB74D', button_click: '#E8C87C', whatsapp_click: '#25D366', form_start: '#FFB74D', form_submit: '#7CB342' };

function Drawer({ visitor, onClose }: { visitor: Visitor; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-[480px] bg-white h-full shadow-modal overflow-y-auto flex flex-col">
        <div className="px-6 py-5 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-[16px] font-semibold text-charcoal-600">Ziyaretçi Detayı</h3>
            <p className="text-[11px] font-mono text-charcoal-300 mt-0.5">{visitor.visitorToken}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-blush-50 text-charcoal-400 transition-colors cursor-pointer">
            <i className="ri-close-line text-[18px]" />
          </button>
        </div>
        <div className="p-6 space-y-5 flex-1">
          {/* Visitor Info */}
          <div className="bg-[#FAF8F4] rounded-xl p-4 space-y-3">
            <h4 className="text-[12px] font-semibold text-charcoal-400 uppercase tracking-wide">Cihaz Bilgisi</h4>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl shadow-card flex-shrink-0">
                <i className={`${deviceIcons[visitor.device]} text-[18px] text-champagne-600`} />
              </div>
              <div>
                <p className="text-[14px] font-semibold text-charcoal-600 capitalize">{visitor.device}</p>
                <p className="text-[12px] text-charcoal-400">{visitor.browser}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <div><p className="text-charcoal-300">İlk Ziyaret</p><p className="font-medium text-charcoal-600 mt-0.5">{visitor.firstVisit}</p></div>
              <div><p className="text-charcoal-300">Son Görülme</p><p className="font-medium text-charcoal-600 mt-0.5">{visitor.lastSeen}</p></div>
              <div><p className="text-charcoal-300">Sayfa Sayısı</p><p className="font-bold text-champagne-600 text-[18px] mt-0.5">{visitor.pageCount}</p></div>
              <div><p className="text-charcoal-300">Etkinlik Sayısı</p><p className="font-bold text-champagne-600 text-[18px] mt-0.5">{visitor.eventCount}</p></div>
            </div>
          </div>
          {/* Source Info */}
          <div className="bg-[#FAF8F4] rounded-xl p-4 space-y-2">
            <h4 className="text-[12px] font-semibold text-charcoal-400 uppercase tracking-wide">Trafik Kaynağı</h4>
            {[
              { label: 'Kaynak', value: visitor.source },
              { label: 'Medyum', value: visitor.medium || '-' },
              { label: 'Kampanya', value: visitor.campaign || '-' },
              { label: 'Açılış Sayfası', value: visitor.landingPage },
            ].map(r => (
              <div key={r.label} className="flex items-start justify-between gap-2">
                <span className="text-[12px] text-charcoal-300 flex-shrink-0">{r.label}</span>
                <span className="text-[12px] font-medium text-charcoal-600 text-right truncate max-w-[240px]">{r.value}</span>
              </div>
            ))}
          </div>
          {/* Timeline */}
          <div>
            <h4 className="text-[12px] font-semibold text-charcoal-400 uppercase tracking-wide mb-4">Etkinlik Zaman Çizelgesi</h4>
            <div className="relative pl-6">
              <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
              <div className="space-y-4">
                {visitorTimeline.map(ev => (
                  <div key={ev.id} className="relative">
                    <div
                      className="absolute -left-4 top-1 w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${eventColors[ev.type]}25`, border: `2px solid ${eventColors[ev.type]}` }}
                    >
                      <div className="w-2 h-2 flex items-center justify-center">
                        <i className={`${eventIcons[ev.type]} text-[8px]`} style={{ color: eventColors[ev.type] }} />
                      </div>
                    </div>
                    <div className="bg-[#FAF8F4] rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[12px] font-semibold text-charcoal-600">{ev.label}</span>
                        <span className="text-[11px] font-mono text-charcoal-300">{ev.time}</span>
                      </div>
                      <p className="text-[11px] text-charcoal-400">{ev.description}</p>
                      {ev.url && <p className="text-[11px] text-champagne-600 font-mono mt-1 truncate">{ev.url}</p>}
                      {ev.button && <span className="inline-block mt-1 text-[10px] font-medium px-2 py-0.5 bg-champagne-50 text-champagne-700 rounded-full">{ev.button}</span>}
                      {ev.duration && <p className="text-[11px] text-charcoal-300 mt-1">{ev.duration}sn izlendi</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Visitors() {
  const [selected, setSelected] = useState<Visitor | null>(null);
  const [search, setSearch] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('Tümü');
  const [statusFilter, setStatusFilter] = useState('Tümü');

  const filtered = visitors.filter(v => {
    const matchSearch = v.visitorToken.includes(search) || v.campaign.includes(search) || v.source.includes(search);
    const matchDevice = deviceFilter === 'Tümü' || v.device === deviceFilter.toLowerCase();
    const matchStatus = statusFilter === 'Tümü' || v.status === (statusFilter === 'Yeni' ? 'new' : 'returning');
    return matchSearch && matchDevice && matchStatus;
  });

  return (
    <Layout>
      {/* Filters */}
      <div className="bg-white rounded-xl2 shadow-card p-5 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <div className="w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
              <i className="ri-search-line text-charcoal-300 text-[14px]" />
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Token veya kaynak ara..." className="w-full h-[38px] pl-9 pr-4 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 placeholder-charcoal-300 focus:outline-none focus:border-champagne-400 transition-colors" />
          </div>
          <div className="flex bg-[#FAF8F4] rounded-xl p-1 gap-1">
            {['Tümü', 'Mobile', 'Desktop', 'Tablet'].map(d => (
              <button key={d} onClick={() => setDeviceFilter(d)} className={`px-3 h-[30px] rounded-lg text-[12px] font-medium transition-all cursor-pointer whitespace-nowrap ${deviceFilter === d ? 'bg-white text-champagne-600 shadow-sm' : 'text-charcoal-400 hover:text-charcoal-600'}`}>{d}</button>
            ))}
          </div>
          <div className="flex bg-[#FAF8F4] rounded-xl p-1 gap-1">
            {['Tümü', 'Yeni', 'Geri Dönen'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 h-[30px] rounded-lg text-[12px] font-medium transition-all cursor-pointer whitespace-nowrap ${statusFilter === s ? 'bg-white text-champagne-600 shadow-sm' : 'text-charcoal-400 hover:text-charcoal-600'}`}>{s}</button>
            ))}
          </div>
          <button className="flex items-center gap-2 h-[38px] px-4 bg-[#FAF8F4] border border-border rounded-xl text-[13px] text-charcoal-500 hover:border-champagne-300 transition-colors cursor-pointer whitespace-nowrap ml-auto">
            <i className="ri-download-line text-[14px]" /> Dışa Aktar
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl2 shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="bg-[#FAF8F4] border-b border-border">
                {['ZİYARETÇİ TOKEN', 'İLK ZİYARET', 'SON GÖRÜLME', 'AÇILIŞ SAYFASI', 'KAYNAK', 'CİHAZ', 'TARAYICI', 'SAYFA', 'ETKİNLİK', 'DURUM', 'İŞLEM'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-charcoal-400 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} className="border-b border-border hover:bg-blush-50 transition-colors cursor-pointer" onClick={() => setSelected(v)}>
                  <td className="px-4 py-4">
                    <span className="font-mono text-[12px] text-charcoal-500 bg-[#FAF8F4] px-2 py-1 rounded-lg">{v.visitorToken}</span>
                  </td>
                  <td className="px-4 py-4 text-[12px] text-charcoal-500 whitespace-nowrap">{v.firstVisit}</td>
                  <td className="px-4 py-4 text-[12px] text-charcoal-400 whitespace-nowrap">{v.lastSeen.split(' ')[1]}</td>
                  <td className="px-4 py-4 max-w-[160px]">
                    <span className="text-[12px] text-charcoal-500 truncate block" title={v.landingPage}>{v.landingPage}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${sourceColors[v.source] || 'bg-charcoal-50 text-charcoal-500'}`}>{v.source}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${deviceIcons[v.device]} text-[14px] text-charcoal-400`} />
                      </div>
                      <span className="text-[12px] text-charcoal-500 capitalize whitespace-nowrap">{v.device}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-[12px] text-charcoal-500 whitespace-nowrap">{v.browser}</td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-[13px] font-semibold text-charcoal-600">{v.pageCount}</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-[13px] font-semibold text-charcoal-600">{v.eventCount}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${v.status === 'new' ? 'bg-green-50 text-green-600' : 'bg-champagne-50 text-champagne-700'}`}>
                      {v.status === 'new' ? 'Yeni' : 'Geri Dönen'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <button onClick={e => { e.stopPropagation(); setSelected(v); }} className="w-8 h-8 flex items-center justify-center rounded-lg text-charcoal-400 hover:text-champagne-600 hover:bg-champagne-50 transition-all cursor-pointer">
                      <i className="ri-eye-line text-[14px]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-4 flex items-center justify-between border-t border-border">
          <span className="text-[12px] text-charcoal-400">Toplam <strong className="text-charcoal-600">{filtered.length}</strong> ziyaretçi</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map(p => (
              <button key={p} className={`w-8 h-8 flex items-center justify-center rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${p === 1 ? 'bg-champagne-500 text-white' : 'text-charcoal-400 hover:bg-[#FAF8F4]'}`}>{p}</button>
            ))}
          </div>
        </div>
      </div>

      {selected && <Drawer visitor={selected} onClose={() => setSelected(null)} />}
    </Layout>
  );
}
